import { EventObject, assign, choose, sendParent } from '@monofront/xstate-v4';
import { CHECKOUT_TRANSITIONS } from '../transitions';
import {
  isBillingEqualShippingChecked,
  isCreatingForBillingAddress,
  isUpdatingOnlyBillingAddress,
} from './guards';
import { SELECT_ADDRESS_STATES } from './states';
import {
  IBillingAddressSelectedEvent,
  ISelectAddressMachineContext,
  IShippingAddressSelectedEvent,
  IAddressCreatedServiceResponse,
  ICreateAddressEvent,
  IAddressUpdatedServiceResponse,
  IAddressListReceived,
  IAddressDeliveryInfoUpdatedEvent,
} from './types';

export const assignUserReady = assign<ISelectAddressMachineContext>({
  isUserReady: true,
});

export const assignOnSelectShippingAddress = assign<
  ISelectAddressMachineContext,
  IShippingAddressSelectedEvent
>({
  shippingAddressSelected: (_, event) => event.addressSelected,
  billingAddressSelected: (context, event) =>
    context.isBillingAndShippingEqual
      ? event.addressSelected
      : context.billingAddressSelected,
});

export const sendToShippingSelectedToParent = sendParent<
  ISelectAddressMachineContext,
  EventObject
>((context) => ({
  type: CHECKOUT_TRANSITIONS.SELECT_SHIPPING_ADDRESS,
  shippingAddress: context.shippingAddressSelected,
  billingAddress: context.billingAddressSelected,
}));

export const sendToBillingSelectedToParent = sendParent<
  ISelectAddressMachineContext,
  EventObject
>((context) => ({
  type: CHECKOUT_TRANSITIONS.SELECT_BILLING_ADDRESS,
  billingAddress: context.billingAddressSelected,
}));

export const assignIsBillingAndShippingNotEqual =
  assign<ISelectAddressMachineContext>({
    isBillingAndShippingEqual: false,
  });

export const assignIsBillingAndShippingEqual =
  assign<ISelectAddressMachineContext>({
    billingAddressSelected: (context) => context.shippingAddressSelected,
    isBillingAndShippingEqual: true,
  });

export const assignBillingAddress = assign<
  ISelectAddressMachineContext,
  IBillingAddressSelectedEvent
>({
  billingAddressSelected: (_, event) => event.addressSelected,
  isBillingAndShippingEqual: (context, event) =>
    context.shippingAddressSelected?.hash === event.addressSelected.hash,
});

export const assignModalOrFirstAddressOnTheList = assign<
  ISelectAddressMachineContext,
  any
>({
  shippingAddressSelected: (context) => {
    const addressPrefilled = context.addressPrefilled;

    if (addressPrefilled) {
      const modalAddressFromList = context.addresses.find(
        (address) =>
          addressPrefilled.street === address.street &&
          addressPrefilled.postalCode === address.postcode &&
          addressPrefilled.city === address.city &&
          addressPrefilled.country === address.countryCode,
      );

      if (modalAddressFromList) {
        return modalAddressFromList;
      }
    }

    const defaultShippingAddress = context.addresses.find(
      (address) => address.default,
    );

    return defaultShippingAddress ?? context.addresses[0];
  },
  billingAddressSelected: (context) => {
    const addressPrefilled = context.addressPrefilled;

    if (addressPrefilled) {
      const modalAddressFromList = context.addresses.find(
        (address) =>
          addressPrefilled.street === address.street &&
          addressPrefilled.postalCode === address.postcode &&
          addressPrefilled.city === address.city &&
          addressPrefilled.country === address.countryCode,
      );

      if (modalAddressFromList) {
        return modalAddressFromList;
      }
    }

    const defaultShippingAddress = context.addresses.find(
      (address) => address.default,
    );

    return defaultShippingAddress ?? context.addresses[0];
  },
});

export const assignCreatedAddress = choose([
  {
    cond: isCreatingForBillingAddress,
    actions: assign<
      ISelectAddressMachineContext,
      IAddressCreatedServiceResponse
    >({
      billingAddressSelected: (_, event) => event.data.data.createAddress,
      addresses: (context, event) => [
        ...context.addresses,
        event.data.data.createAddress,
      ],
      isAddressEmpty: () => false,
    }),
  },
  {
    actions: assign<
      ISelectAddressMachineContext,
      IAddressCreatedServiceResponse
    >({
      shippingAddressSelected: (_, event) => event.data.data.createAddress,
      billingAddressSelected: (context, event) =>
        context.isBillingAndShippingEqual
          ? event.data.data.createAddress
          : context.billingAddressSelected,
      addresses: (context, event) => [
        ...context.addresses,
        event.data.data.createAddress,
      ],
      isAddressEmpty: (_) => false,
    }),
  },
]);

export const assignCreateForBillingOnly = assign<
  ISelectAddressMachineContext,
  ICreateAddressEvent
>({
  creatingForBillingOnly: (_, event) => event.creatingForBillingOnly,
});

export const selectCreatedAddress = choose([
  {
    actions: sendToBillingSelectedToParent,
    cond: isCreatingForBillingAddress,
  },
  {
    actions: sendToShippingSelectedToParent,
  },
]);

export const selectUpdatedAddress = choose([
  {
    actions: sendToBillingSelectedToParent,
    cond: isUpdatingOnlyBillingAddress,
  },
  {
    actions: sendToShippingSelectedToParent,
  },
]);

export const assignAddressUpdated = choose([
  {
    cond: isUpdatingOnlyBillingAddress,
    actions: assign<
      ISelectAddressMachineContext,
      IAddressUpdatedServiceResponse
    >({
      billingAddressSelected: (_, event) => event.data.data.updateAddress,
      addresses: (context, event) => {
        const response = event.data.data.updateAddress;

        const addressUpdated =
          context.addresses.find(({ id }) => id === response.id) || {};

        Object.assign(addressUpdated, response);

        return context.addresses;
      },
    }),
  },
  {
    actions: assign<
      ISelectAddressMachineContext,
      IAddressUpdatedServiceResponse
    >({
      shippingAddressSelected: (_, event) => event.data.data.updateAddress,
      billingAddressSelected: (_, event) => event.data.data.updateAddress,
      addresses: (context, event) => {
        const updatedAddress = event.data.data.updateAddress;
        const addresses = context.addresses.map((address) =>
          address.id === updatedAddress.id ? updatedAddress : address,
        );

        return addresses;
      },
    }),
  },
]);

export const assignDeliveryInfoToRelatedAddress = assign<
  ISelectAddressMachineContext,
  IAddressDeliveryInfoUpdatedEvent
>({
  addresses: (context, event) => {
    const addressHash =
      event.selectedShippingAddressHash ??
      context.shippingAddressSelected?.hash;

    const addresses = context.addresses.map((address) =>
      address.hash !== addressHash
        ? address
        : { ...address, deliveryInstruction: event.deliveryInstruction },
    );

    return addresses;
  },
  shippingAddressSelected: (context, event) => {
    if (!context.shippingAddressSelected) {
      return null;
    }

    return {
      ...context.shippingAddressSelected,
      deliveryInstruction: event.deliveryInstruction,
    };
  },
});

export const assignAddressListReceived = assign<
  ISelectAddressMachineContext,
  IAddressListReceived
>({
  addresses: (_, event) => event.data,
  shippingAddressSelected: (context, event) => {
    const selected = context.shippingAddressSelected;

    if (!selected?.hash) {
      return selected;
    }

    return event.data.find(({ hash }) => hash === selected.hash) ?? selected;
  },
  billingAddressSelected: (context, event) => {
    const selected = context.billingAddressSelected;

    if (!selected?.hash) {
      return selected;
    }

    return event.data.find(({ hash }) => hash === selected.hash) ?? selected;
  },
});

export const selectBillingEqualShippingAddress = choose([
  {
    cond: isBillingEqualShippingChecked,
    actions: [assignIsBillingAndShippingNotEqual],
  },
  {
    actions: [assignIsBillingAndShippingEqual, sendToBillingSelectedToParent],
  },
]);

export enum SELECT_ADDRESS_EXTERNAL_ACTIONS {
  updateAddressError = 'updateAddressError',
  createAddressError = 'createAddressError',
}

export const selectShippingAddressTransition = {
  target: SELECT_ADDRESS_STATES.ADDRESS_SELECTED,
  actions: [assignOnSelectShippingAddress, sendToShippingSelectedToParent],
};

export const selectBillingAddressTransition = {
  target: SELECT_ADDRESS_STATES.ADDRESS_SELECTED,
  actions: [assignBillingAddress, sendToBillingSelectedToParent],
};
